import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import NotificationsNone from '@mui/icons-material/NotificationsNone'
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined'
import PlaceOutlined from '@mui/icons-material/PlaceOutlined'
import { CHROME_GAP, CHROME_RADIUS, TOUCH_MIN } from 'core/theme.tokens'
import { useAuth } from 'hooks/auth/useAuth'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import {
  queryDevicePermission,
  readNotificationPermission,
  requestCameraAccess,
  requestLocationAccess,
  requestNotificationPermission,
  shouldPromptDeviceAccess,
} from 'core/deviceAccess'
import { registerPushSubscription } from 'core/push/registerPushSubscription'
import { COMMAND_PALETTE_DEMO_ID } from 'components/command-palette/CommandPaletteOnboarding'
import { PORTAL_ASSISTANT_DEMO_ID } from 'components/assistant/PortalAssistantOnboarding'
import { featureDemoStorageId, shouldShowFeatureDemo } from 'components/feature-demo/featureDemoStorage'
import { isPortalAssistantDemoAudience } from 'components/assistant/portalAssistantDemoAudience'
import { FEATURE_FLAGS } from '@/config/featureFlags'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { ASSISTANT_FEATURE_FLAG_ID } from 'constants/featureFlags'

const DISMISS_KEY = 'otw-device-access-dismissed'

const ACCESS_ITEMS = [
  { icon: NotificationsNone, label: 'Notifications', detail: 'Alerts when you are away from the portal' },
  { icon: PlaceOutlined, label: 'Location', detail: 'Maps and pickup directions on this phone' },
  { icon: PhotoCameraOutlined, label: 'Camera', detail: 'Title photos and vehicle pictures' },
] as const

export const DeviceAccessPrompt: React.FC = () => {
  const { currentUser, userInfo } = useAuth()
  const assistantEnabled = useFeatureFlag(ASSISTANT_FEATURE_FLAG_ID)
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [deniedBanner, setDeniedBanner] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!isMobile || !currentUser?.uid) return
    const dismissed = sessionStorage.getItem(DISMISS_KEY) === '1'
    const permission = readNotificationPermission()
    setDeniedBanner(permission === 'denied')
    if (permission === 'granted') {
      void registerPushSubscription(currentUser.uid)
    }
    let cancelled = false
    void Promise.all([queryDevicePermission('geolocation'), queryDevicePermission('camera')]).then(
      ([locationPermission, cameraPermission]) => {
        if (cancelled) return
        const commandPalettePending = shouldShowFeatureDemo(
          featureDemoStorageId(COMMAND_PALETTE_DEMO_ID, currentUser.uid),
          { enabled: FEATURE_FLAGS.commandPaletteOnboarding },
        )
        const assistantPending =
          userInfo?.role === 'admin' &&
          isPortalAssistantDemoAudience(userInfo?.email) &&
          assistantEnabled &&
          shouldShowFeatureDemo(featureDemoStorageId(PORTAL_ASSISTANT_DEMO_ID, currentUser.uid), {
            enabled: FEATURE_FLAGS.portalAssistantOnboarding,
          })
        setOpen(
          !commandPalettePending &&
            !assistantPending &&
            shouldPromptDeviceAccess({
              notificationPermission: permission,
              locationPermission,
              cameraPermission,
              dismissedThisSession: dismissed,
            }),
        )
      },
    )
    return () => {
      cancelled = true
    }
  }, [isMobile, currentUser?.uid, userInfo?.role, userInfo?.email, assistantEnabled])

  if (!isMobile || !currentUser?.uid) return null

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1')
    setOpen(false)
  }

  const allow = async () => {
    setBusy(true)
    try {
      const notificationPromise = requestNotificationPermission()
      const locationPromise = requestLocationAccess()
      const cameraPromise = requestCameraAccess()
      const notification = await notificationPromise
      if (notification === 'granted') {
        await registerPushSubscription(currentUser.uid)
        setDeniedBanner(false)
      } else if (notification === 'denied') {
        setDeniedBanner(true)
      }
      await Promise.all([locationPromise, cameraPromise])
    } finally {
      setBusy(false)
      dismiss()
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={dismiss}
        fullWidth={false}
        maxWidth={false}
        fullScreen={false}
        PaperProps={{
          sx: theme => ({
            mx: `${CHROME_GAP}px`,
            my: 3,
            width: `calc(100% - ${CHROME_GAP * 2}px)`,
            maxWidth: 400,
            maxHeight: 'calc(100% - 48px)',
            borderRadius: `${CHROME_RADIUS}px`,
            overflow: 'hidden',
            '&::before, &::after': { display: 'none' },
            [theme.breakpoints.down('sm')]: {
              marginLeft: `${CHROME_GAP}px`,
              marginRight: `${CHROME_GAP}px`,
              marginTop: theme.spacing(3),
              marginBottom: theme.spacing(3),
              width: `calc(100% - ${CHROME_GAP * 2}px)`,
              maxHeight: 'calc(100% - 48px)',
              borderRadius: `${CHROME_RADIUS}px`,
            },
          }),
        }}
      >
        <DialogTitle sx={{ px: 2.5, pt: 2.5, pb: 1, fontSize: 18, fontWeight: 700 }}>
          Allow phone access
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 1 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            The portal needs these on this phone. You can change them later in Settings.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {ACCESS_ITEMS.map(item => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: 'primary.main',
                  }}
                >
                  <item.icon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {item.detail}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 2, gap: 1 }}>
          <Button onClick={dismiss} disabled={busy} sx={{ minHeight: TOUCH_MIN }}>
            Not now
          </Button>
          <Button onClick={allow} variant='contained' disabled={busy} sx={{ minHeight: TOUCH_MIN, minWidth: 96 }}>
            Allow
          </Button>
        </DialogActions>
      </Dialog>
      {deniedBanner && !open ? (
        <Alert
          severity='warning'
          action={
            <Button color='inherit' size='small' onClick={() => setOpen(true)}>
              Turn on
            </Button>
          }
          sx={{ mx: 2, mt: 1, borderRadius: `${CHROME_RADIUS}px` }}
        >
          Notifications are off. Turn them on in this prompt, or in the phone Settings app if iOS blocked them.
        </Alert>
      ) : null}
    </>
  )
}
